import React, { useState } from 'react';
import styled from 'styled-components';
import RoomList from './RoomList';
import ChatForm from './ChatForm';
import Conversation from './Conversation';
import Navigation from './Navigation';
import LogViewer from './LogViewer'; // Ensure you have this component
import { useChat } from '../context/ChatProvider';
import { Description } from '../styled/Description';
import { IoIosClose } from 'react-icons/io';
import { useTranslation } from 'react-i18next';

const Header = styled.header`
    display: flex;
    align-items: center;
    gap: 1em;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding-bottom: 1em;
    height: 3.2em;
    width: 100%;

    & img {
        height: 100%;
        border-radius: 0.7em;
    }

    & h2 {
        font-size: 0.85em;
        font-weight: 600;
        color: var(--blue-button-color);
    }
`;

const ChatAppContainer = styled.div`
    --vertical-padding: 3vh;
    display: flex;
    gap: 0vw;
    height: 80vh;
    width: 80vw;
    justify-content: space-between;
    background: #e5e7e8;
    box-shadow: rgba(0, 0, 0, 0.25) 0px 54px 55px,
    rgba(0, 0, 0, 0.12) 0px -12px 30px,
    rgba(0, 0, 0, 0.12) 0px 4px 6px,
    rgba(0, 0, 0, 0.17) 0px 12px 13px,
    rgba(0, 0, 0, 0.09) 0px -3px 5px;

    @media (max-width: 820px) {
        position: relative;
        width: 100%;
        height: 100vh;
        flex-direction: column-reverse;
        font-size: 0.85rem;
        gap: 0;
    }
`;

const CenterContainer = styled.div`
    display: flex;
    flex: 1;
    gap: 1.5vw;
    flex-direction: column;
    height: 100%;
    margin: auto 0;
    padding: 3vw 3vw;

    @media (max-width: 820px) {
        height: 80%;
    }
`;

const Chat = styled.div`
    padding: var(--vertical-padding) var(--vertical-padding) 1.5vh var(--vertical-padding);
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 80%;
    background: #fff;
    border-radius: 30px;

    @media (max-width: 820px) {
        margin: 0 2.5vw;
    }
`;

const ResetButtonContainer = styled.div`
    display: flex;
    align-items: center;
`;

const ResetButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: red;
    border: none;
    cursor: pointer;
    color: white;
    font-size: 20px;
`;

const WelcomeMessageContainer = styled.div`
    margin: auto 0;
    font-size: 0.9em;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
    overflow-y: auto;
    padding: 2em;

    h2 {
        color: var(--blue-button-color);
        font-size: 1.2em;
        margin-bottom: 1em;
    }

    p {
        margin: 1em 0;
        font-size: 0.9em;
        line-height: 1.5;
        color: rgba(0, 0, 0, 0.7);
    }

    p:last-of-type {
        color: var(--blue-button-color);
        font-weight: bold;
        text-align: center;
    }
`;

const WelcomeMessage = () => {
    const { t } = useTranslation();

    return (
        <WelcomeMessageContainer>
            <h2>{t('welcomeMessage.title')}</h2>
            {t('welcomeMessage.paragraphs', { returnObjects: true }).map((paragraph, index) => (
                <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
            ))}
        </WelcomeMessageContainer>
    );
};

const ChatContainer = () => {
    const [query, setQuery] = useState('');
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [viewMode, setViewMode] = useState('chat'); // 'chat' or 'logs'
    const { currentRoom, resetCurrentRoom } = useChat();

    return (
        <ChatAppContainer>
            <Navigation openRoomNav={() => setIsNavOpen(true)} />
            <CenterContainer>
                {viewMode === 'chat' ? (
                    <Chat>
                        {!currentRoom ? (
                            <WelcomeMessage />
                        ) : (
                            <>
                                <Header>
                                    <div>
                                        <ResetButtonContainer>
                                            <ResetButton onClick={() => resetCurrentRoom()}>
                                                <IoIosClose />
                                            </ResetButton>
                                        </ResetButtonContainer>
                                    </div>
                                    <div>
                                        <h2>{currentRoom.name}</h2>
                                        <Description color="#000" size="0.75em">
                                            {currentRoom.description}
                                        </Description>
                                    </div>
                                </Header>

                                <Conversation />
                                <ChatForm />
                            </>
                        )}
                    </Chat>
                ) : (
                    <Chat>
                        <LogViewer />
                    </Chat>
                )}
            </CenterContainer>
            <RoomList
                query={query}
                isNavOpen={isNavOpen}
                setIsNavOpen={setIsNavOpen}
                toggleLogView={() => setViewMode(viewMode === 'chat' ? 'logs' : 'chat')}
            />
        </ChatAppContainer>
    );
};

export default ChatContainer;
